import { CheckCircle2, XCircle } from 'lucide-react';

/**
 * Regex enforcing the full password policy:
 * - At least 12 characters
 * - At least one uppercase letter (A–Z)
 * - At least one lowercase letter (a–z)
 * - At least one digit (0–9)
 * - At least one special character from the allowed set
 *
 * Exported so Zod schemas and other validators can import it instead of
 * duplicating the pattern.
 */
export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

interface Rule {
  label: string;
  test: (password: string) => boolean;
}

const RULES: Rule[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { label: 'Number (0–9)', test: (p) => /\d/.test(p) },
  {
    label: 'Special character (!@#$%^&* etc.)',
    test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p),
  },
];

interface PasswordStrengthChecklistProps {
  password: string;
}

const PasswordStrengthChecklist = ({ password }: PasswordStrengthChecklistProps) => {
  if (password.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1">
      {RULES.map((rule) => {
        const met = rule.test(password);
        return (
          <li key={rule.label} className="flex items-center gap-2 text-xs">
            {met ? (
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-green-500" />
            ) : (
              <XCircle className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
            )}
            <span className={met ? 'text-green-700 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'}>
              {rule.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default PasswordStrengthChecklist;
